"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";

import Link from "next/link";
import { useRouter } from "next/navigation";

import { motion } from "framer-motion";
import { Check, Copy, Eye, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { EnrichmentRenderer } from "@/components/debug/EnrichmentRenderer";
import DebugJsonModal from "@/components/ui/DebugJsonModal";
import MoreLikeThisModal, { type MoreLikeThisSearchData } from "@/components/ui/MoreLikeThisModal";
import { QuickviewModal } from "@/components/ui/QuickviewModal";
// import RatingDisplay from "@/components/ui/RatingDisplay";
import SaveButton from "@/components/ui/SaveButton";
import { useDebug } from "@/contexts/DebugContext";
import { useSavedProducts } from "@/contexts/SavedProductsContext";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { useProductImageMetadata } from "@/hooks/useProductImageMetadata";
import { dispatchMltSearch } from "@/lib/mlt-utils";
import { cacheProductForPDP, useRegisterProducts } from "@/lib/product-store";
import {
  PRODUCT_NAVIGATION_UNAVAILABLE_MESSAGE,
  buildCanonicalProductPathFromRecord,
  warnMissingCanonicalProductPath,
} from "@/lib/product-url";
import {
  type Product,
  type ProductImageSource,
  getFormattedPrice,
  getProductAnyId,
  getProductImage,
  getProductImageSources,
  getProductName,
  getSavedProductId,
  // getRatingForDisplay,
} from "@/lib/product-utils";
import { RESULTS_PATH_PREFIX } from "@/lib/route-paths";
import { hydrateProductByUuid } from "@/lib/shop-agent-client";
import { tailwindBreakpoints } from "@/styles/design-tokens";

import ImageCarousel from "./ImageCarousel";

// Helper function to capitalize first letter (outside component to avoid recreation)
const capitalizeFirstLetter = (str: string): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

function hasFullProductPayload(product: Product | null): boolean {
  if (!product) return false;
  const record = product as unknown as Record<string, unknown>;
  return (
    record.enrichment !== undefined ||
    record.description !== undefined ||
    record.hasVariant !== undefined ||
    record.offers !== undefined
  );
}

interface ProductCardProps {
  product: Product;
  showSavedLabel?: boolean;
  /** Render a smaller Say More button and overlay (used when side drawer visible) */
  compact?: boolean;
  defaultCollectionId?: string;
  /** When true, eagerly load the first carousel image for LCP */
  priorityFirst?: boolean;
  /** Optional existing thread/search ID to use for MLT (if on results page) */
  existingThreadId?: string;
  /** Callback invoked before navigating to PDP (for caching scroll position) */
  onBeforeNavigateToPDP?: () => void;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  priorityFirst = false,
  existingThreadId,
  onBeforeNavigateToPDP,
}) => {
  const router = useRouter();

  const [expanded, setExpanded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [showMoreLikeModal, setShowMoreLikeModal] = useState(false);
  const [cardHovered, setCardHovered] = useState(false);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [copiedFullProduct, setCopiedFullProduct] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [hydratedProduct, setHydratedProduct] = useState<Product | null>(null);

  useRegisterProducts(product);

  const productId = getProductAnyId(product);
  const productForDetails = hydratedProduct ?? product;
  const hydrateFullProduct = useCallback(async (): Promise<Product> => {
    if (hydratedProduct) return hydratedProduct;
    if (!productId || productId === "unknown") return product;

    const fetched = (await hydrateProductByUuid(productId)) as unknown as Product;
    setHydratedProduct(fetched);
    return fetched;
  }, [hydratedProduct, product, productId]);

  useEffect(() => {
    setHydratedProduct(null);
  }, [productId]);

  const { isDebug } = useDebug();
  const enrichmentData = useMemo<Record<string, unknown>>(() => {
    const { enrichment } = productForDetails;
    if (enrichment && typeof enrichment === "object") {
      return enrichment as unknown as Record<string, unknown>;
    }
    return { note: "no enrichment" };
  }, [productForDetails]);

  useBodyScrollLock({
    key: `product-card-expanded:${productId}`,
    enabled: expanded,
    minWidthPx: tailwindBreakpoints.sm,
  });

  useEffect(() => {
    if (!isDebug || !cardHovered) return;
    void hydrateFullProduct().catch((error) => {
      console.warn("Failed to hydrate product for debug overlay", productId, error);
    });
  }, [cardHovered, hydrateFullProduct, isDebug, productId]);

  const { isSaved } = useSavedProducts();
  const savedProductId = getSavedProductId(product, isSaved);

  // Memoize product images prioritizing CDN-resolved primary image
  const productImageSources = useMemo(() => {
    if (!product) return [] as ProductImageSource[];

    const sources = getProductImageSources(product);
    if (sources.length > 0) {
      return sources;
    }

    const primary = getProductImage(product);
    if (primary && primary !== "/placeholder.jpg") {
      return [{ primary }];
    }

    return [] as ProductImageSource[];
  }, [product]);

  useEffect(() => {
    setImgError(false);
  }, [productImageSources]);

  // Use the separated image metadata hook
  const {
    imageWrapperClass,
    dynamicAspectRatio,
    objectFit,
    handleImageLoad,
    needsWhiteBackground,
  } = useProductImageMetadata(product);

  const handleImageError = () => {
    console.warn(`Image failed to load for product: ${getProductName(product)}`);
    setImgError(true);
  };

  // Get brand name with fallback to capitalized catalog name
  const getBrandName = (): string => {
    // Prefer enrichment canonical brand name if available
    if (productForDetails.enrichment?.canonical_brand?.brand_name) {
      return productForDetails.enrichment.canonical_brand.brand_name;
    }
    if (productForDetails.brand_name) return productForDetails.brand_name;
    if (productForDetails.catalog) return capitalizeFirstLetter(productForDetails.catalog);
    return "Brand";
  };

  // Ensure product is not null
  if (!product) {
    return <div className="rounded bg-white p-2 text-sm shadow-sm">Product data unavailable</div>;
  }

  const productPath = buildCanonicalProductPathFromRecord(
    product as unknown as Record<string, unknown>,
    productId
  );

  const prepareProductNavigation = () => {
    try {
      if (hasFullProductPayload(productForDetails)) {
        cacheProductForPDP(productId, productForDetails);
      }
    } catch {
      // localStorage unavailable or quota exceeded
    }

    onBeforeNavigateToPDP?.();
  };

  const getProductDestination = (path: string): string => {
    const supportsReturnUrl =
      typeof window !== "undefined" &&
      (window.location.pathname.startsWith(RESULTS_PATH_PREFIX) ||
        window.location.pathname.startsWith("/catalog/"));

    if (!supportsReturnUrl) {
      return path;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const qs = urlParams.toString();
    const returnUrl = `${window.location.pathname}${qs ? `?${qs}` : ""}`;
    return `${path}?returnUrl=${encodeURIComponent(returnUrl)}`;
  };

  const handlePrimaryActivate = () => {
    if (!productId || productId === "unknown") return;
    if (!productPath) {
      warnMissingCanonicalProductPath("ProductCard", productId);
      toast.error(PRODUCT_NAVIGATION_UNAVAILABLE_MESSAGE);
      return;
    }

    prepareProductNavigation();
    router.push(getProductDestination(productPath));
  };

  const handleProductLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
    if (!productPath) {
      return;
    }
    prepareProductNavigation();

    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();
    router.push(getProductDestination(productPath));
  };

  const handleQuickView = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await hydrateFullProduct();
    } catch (error) {
      console.warn("Failed to hydrate product for quick view", productId, error);
    }
    setExpanded(true);
  };

  // Get price information for display
  const price = getFormattedPrice(product);

  // Handle More button click
  const handleMoreClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await hydrateFullProduct();
    } catch (error) {
      console.warn("Failed to hydrate product for more-like-this", productId, error);
    }
    setShowMoreLikeModal(true);
  };

  // Handle right-click in debug mode
  const handleContextMenu = (e: React.MouseEvent) => {
    if (isDebug) {
      e.preventDefault();
      e.stopPropagation();
      void hydrateFullProduct().catch((error) => {
        console.warn("Failed to hydrate product for debug JSON", productId, error);
      });
      setShowJsonModal(true);
    }
  };

  const handleMoreLikeSubmit = (searchData: MoreLikeThisSearchData) => {
    setShowMoreLikeModal(false);
    const { navigateTo } = dispatchMltSearch(searchData, existingThreadId);
    if (navigateTo) router.push(navigateTo);
  };

  return (
    <>
      <div
        className={`group relative flex h-fit w-full cursor-pointer flex-col overflow-hidden rounded bg-white shadow-sm transition-all hover:shadow-md ${showMoreLikeModal ? "z-30" : ""}`}
        onClick={handlePrimaryActivate}
        onContextMenu={handleContextMenu}
        onMouseEnter={() => setCardHovered(true)}
        onMouseLeave={() => setCardHovered(false)}
        data-testid="product-card"
        data-track-product="true"
        data-product-uuid={productId}
        data-track-action="click"
        data-product-name={getProductName(product)}
        role={productPath ? undefined : "button"}
        tabIndex={productPath ? undefined : 0}
        aria-label={
          productPath
            ? undefined
            : `View details for ${getProductName(product)} from ${getBrandName()}`
        }
        onKeyDown={
          productPath
            ? undefined
            : (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handlePrimaryActivate();
                }
              }
        }
      >
        {/* Save Button - Top Right */}
        <div className="absolute right-4 top-4 z-20">
          <SaveButton productId={savedProductId} product={product} variant="icon" className="" />
        </div>

        <div
          className={`${imageWrapperClass}${needsWhiteBackground ? " bg-white" : ""}`}
          style={dynamicAspectRatio ? { aspectRatio: dynamicAspectRatio } : undefined}
        >
          {imgError ? (
            // Skeleton placeholder for broken images
            <div className="relative flex aspect-square w-full animate-pulse items-center justify-center bg-gradient-to-br from-gray-100 via-gray-200 to-gray-100">
              {/* Image icon */}
              <div className="flex flex-col items-center gap-1">
                <div className="rounded-full bg-white/50 p-2">
                  <svg
                    className="h-8 w-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <span className="text-xs font-medium text-gray-500">Image unavailable</span>
              </div>
            </div>
          ) : (
            <ImageCarousel
              images={productImageSources}
              productName={getProductName(product)}
              objectFit={objectFit}
              onImageLoad={handleImageLoad}
              onImageError={handleImageError}
              className=""
              priorityFirst={priorityFirst}
              autoHoverSwap
            />
          )}

          {/* Debug: AI Agent Clickable Button Overlay */}
          {isDebug && !cardHovered && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePrimaryActivate();
              }}
              className="absolute inset-0 z-40 flex flex-col items-center justify-center gap-2 border-4 border-blue-600 bg-blue-600/20 transition-all hover:bg-blue-600/40"
              aria-label={`AI Agent: Click to open ${getProductName(product)}`}
            >
              <div className="rounded-lg bg-blue-600 px-3 py-2 text-center font-mono text-xs font-bold text-white shadow-lg">
                <div className="mb-1 text-[11px] text-yellow-300">
                  🤖 MCP browser_click SELECTOR:
                </div>
                <div className="rounded border border-yellow-300/50 bg-black/40 px-2 py-1">
                  AI Agent: Click to open {getProductName(product)}
                </div>
                <div className="mt-1 text-[9px] text-green-200">↑ Use EXACTLY this text ↑</div>
              </div>
            </button>
          )}

          {/* Debug: Enrichment overlay on hover */}
          {isDebug && cardHovered && (
            <div
              className="absolute inset-0 z-[60] flex flex-col"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                pointerEvents: "auto",
                backgroundColor: "rgba(0, 0, 0, 0.85)",
                color: "#ffffff",
              }}
            >
              {/* Sticky header with copy button */}
              <div
                className="sticky top-0 z-[100] flex flex-col gap-2 border-b px-2 py-2"
                style={{
                  backgroundColor: "rgba(0, 0, 0, 0.95)",
                  borderColor: "rgba(255, 255, 255, 0.2)",
                }}
              >
                <div className="font-semibold" style={{ color: "rgba(255, 255, 255, 0.9)" }}>
                  Debug: Enrichment
                </div>
                <div className="flex flex-nowrap items-center gap-1.5 overflow-x-auto">
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      try {
                        await navigator.clipboard.writeText(
                          JSON.stringify(productForDetails, null, 2)
                        );
                        setCopiedFullProduct(true);
                        setTimeout(() => setCopiedFullProduct(false), 2000);
                      } catch (err) {
                        console.error("Failed to copy product payload:", err);
                      }
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    className="flex flex-shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded px-2 py-1 text-[10px] font-medium transition-colors sm:text-xs"
                    title="Copy full product payload"
                    style={{
                      pointerEvents: "auto",
                      backgroundColor: "#2563eb",
                      color: "#ffffff",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#1d4ed8";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#2563eb";
                    }}
                  >
                    {copiedFullProduct ? (
                      <>
                        <Check className="h-3 w-3" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3" />
                        <span>Copy Data</span>
                      </>
                    )}
                  </button>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      try {
                        const url = productForDetails.url || "";
                        await navigator.clipboard.writeText(url);
                        setCopiedUrl(true);
                        setTimeout(() => setCopiedUrl(false), 2000);
                      } catch (err) {
                        console.error("Failed to copy product URL:", err);
                      }
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                    className="flex flex-shrink-0 cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded px-2 py-1 text-[10px] font-medium transition-colors sm:text-xs"
                    title="Copy product URL"
                    style={{
                      pointerEvents: "auto",
                      backgroundColor: "#059669",
                      color: "#ffffff",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#047857";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#059669";
                    }}
                  >
                    {copiedUrl ? (
                      <>
                        <Check className="h-3 w-3" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3" />
                        <span>Copy URL</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
              {/* Scrollable content area */}
              <div className="flex-1 overflow-y-auto p-2">
                <EnrichmentRenderer data={enrichmentData} />
              </div>
            </div>
          )}

          {/* Quick View - Desktop only, appears on hover */}
          <motion.button
            className="absolute bottom-4 left-4 z-20 hidden items-center gap-1.5 overflow-hidden rounded-full bg-white/90 text-gray-800 shadow-sm backdrop-blur-sm transition-colors duration-200 hover:bg-white hover:text-black md:flex"
            onClick={handleQuickView}
            style={{
              filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.15))",
              pointerEvents: cardHovered ? "auto" : "none",
            }}
            initial={false}
            animate={{
              opacity: cardHovered ? 1 : 0,
              y: cardHovered ? 0 : 6,
              paddingLeft: 10,
              paddingRight: 12,
              paddingTop: 6,
              paddingBottom: 6,
            }}
            transition={{
              duration: 0.2,
              ease: [0.4, 0, 0.2, 1],
            }}
            aria-label={`Quick view ${getProductName(product)}`}
            tabIndex={cardHovered ? 0 : -1}
          >
            <Eye className="h-4 w-4 flex-shrink-0" />
            <span className="whitespace-nowrap text-xs font-medium">Quick View</span>
          </motion.button>

          {/* More Like This - Desktop: expand animation on hover, Mobile: simple icon */}
          <div className="absolute bottom-4 right-4 z-20">
            {/* Desktop version with hover expand animation */}
            <motion.button
              className="hidden items-center gap-1.5 overflow-hidden rounded-full bg-black/40 text-white/90 backdrop-blur-[1px] transition-colors duration-200 hover:bg-black/50 hover:text-white sm:flex"
              onClick={handleMoreClick}
              style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.35))" }}
              initial={false}
              animate={{
                width: cardHovered ? "132px" : "34px",
                paddingLeft: cardHovered ? 8 : 6,
                paddingRight: cardHovered ? 10 : 6,
                paddingTop: 6,
                paddingBottom: 6,
              }}
              transition={{
                duration: 0.25,
                ease: [0.4, 0, 0.2, 1],
              }}
            >
              <Sparkles className="h-5 w-5 flex-shrink-0" />
              <motion.span
                className="whitespace-nowrap text-xs font-medium"
                initial={false}
                animate={{
                  opacity: cardHovered ? 1 : 0,
                }}
                transition={{
                  duration: cardHovered ? 0.2 : 0.15,
                  delay: cardHovered ? 0.05 : 0,
                  ease: "easeOut",
                }}
              >
                More Like This
              </motion.span>
            </motion.button>

            {/* Mobile version - simple icon, no animation */}
            <button
              className="rounded-full bg-black/40 p-1.5 text-white/90 backdrop-blur-[1px] transition-colors duration-200 hover:bg-black/50 hover:text-white sm:hidden"
              onClick={handleMoreClick}
              style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.35))" }}
            >
              <Sparkles className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex flex-col space-y-0.5 p-0.5 sm:space-y-1 sm:p-2">
          {/* Brand and price row (mobile) or stacked (desktop) */}
          <div className="flex w-full justify-between sm:flex-col sm:space-y-1">
            <p className="truncate text-[9px] font-medium leading-tight text-gray-600 sm:text-xs">
              {getBrandName()}
            </p>
            {price.final && (
              <span
                data-testid="product-price"
                className="flex-shrink-0 text-[10px] font-semibold text-gray-600 sm:text-sm"
              >
                {price.final}
                {price.original && (
                  <span className="ml-0.5 text-[9px] text-gray-500 line-through sm:text-xs">
                    {price.original}
                  </span>
                )}
              </span>
            )}
          </div>
          {/* Product name */}
          <h3 className="line-clamp-1 text-[10px] font-semibold leading-tight sm:text-sm">
            {productPath ? (
              <Link
                href={productPath}
                onClick={handleProductLinkClick}
                aria-label={`View details for ${getProductName(product)} from ${getBrandName()}`}
                data-testid="product-card-canonical-link"
                className="hover:underline"
              >
                {getProductName(product)}
              </Link>
            ) : (
              getProductName(product)
            )}
          </h3>

          {/* Rating section hidden on preview cards
          <div className="mt-0.5">
            <div className="sm:hidden">
              <RatingDisplay
                rating={getRatingForDisplay(product)}
                size="xs"
                showCount={false}
                showValue={false}
              />
            </div>
            <div className="hidden sm:block">
              <RatingDisplay rating={getRatingForDisplay(product)} size="xs" showCount={false} />
            </div>
          </div>
          */}
        </div>
      </div>

      {expanded && (
        <QuickviewModal
          product={productForDetails}
          isOpen={expanded}
          onClose={() => {
            setExpanded(false);
          }}
          onSeeFullDetails={() => {
            cacheProductForPDP(productId, productForDetails);
            onBeforeNavigateToPDP?.();

            const productPath = buildCanonicalProductPathFromRecord(
              productForDetails as unknown as Record<string, unknown>,
              productId
            );
            if (!productPath) {
              warnMissingCanonicalProductPath("ProductCard", productId);
              toast.error(PRODUCT_NAVIGATION_UNAVAILABLE_MESSAGE);
              return;
            }

            const params = new URLSearchParams();
            const supportsReturnUrl =
              typeof window !== "undefined" &&
              (window.location.pathname.startsWith(RESULTS_PATH_PREFIX) ||
                window.location.pathname.startsWith("/catalog/"));
            if (supportsReturnUrl) {
              const urlParams = new URLSearchParams(window.location.search);
              const qs = urlParams.toString();
              const returnUrl = `${window.location.pathname}${qs ? `?${qs}` : ""}`;
              params.set("returnUrl", returnUrl);
            }
            router.push(`${productPath}?${params.toString()}`);
          }}
          onMoreLikeThis={() => {
            setExpanded(false);
            setShowMoreLikeModal(true);
          }}
        />
      )}

      {showMoreLikeModal && (
        <MoreLikeThisModal
          product={productForDetails}
          onClose={() => setShowMoreLikeModal(false)}
          onSubmit={handleMoreLikeSubmit}
        />
      )}

      {/* Debug JSON Modal */}
      {isDebug && (
        <DebugJsonModal
          isOpen={showJsonModal}
          onClose={() => setShowJsonModal(false)}
          data={productForDetails}
          title={`Product JSON: ${getProductName(product)}`}
        />
      )}
    </>
  );
};

export default ProductCard;
